import type { Dispatch, SetStateAction } from "react";

import type { OpenAPI } from "simplydo/interfaces";
import type { UserMe } from "simplydo/interfaces/types/userTypes";
import type { SemanticCOLORS, SemanticICONS } from "simplydo/react/ui";

export type RolePermission = OpenAPI.Schemas["Role"]["permissions"][number];

/** Role with resolved user count and owner scopes from GET /roles. */
export type RoleWithInformation = NonNullable<
  OpenAPI.GET<"/roles/{for_type}/{for_id}/roles">["response"]["roles"]
>[number];

export type RoleUserRolesResponse =
  OpenAPI.GET<"/roles/{for_type}/{for_id}/users/{user_id}">["response"];

export type IRole = RoleWithInformation;

export type RolesUser = UserMe | null;

export type RoleIconName = NonNullable<
  NonNullable<OpenAPI.Schemas["Role"]["icon"]>["name"]
>;

export type RoleIconColour = NonNullable<
  NonNullable<OpenAPI.Schemas["Role"]["icon"]>["colour"]
>;

export type RoleEditorIcon = { name?: RoleIconName; colour?: RoleIconColour };

export type RoleForType = OpenAPI.Schemas["Role"]["forType"];

export type RoleForTypeLabelKey =
  | "organisation"
  | "group"
  | "global"
  | "ideaBusinessProfile"
  | "challenge";

export const ROLE_FOR_TYPE_LABELS: Record<RoleForTypeLabelKey, string> = {
  organisation: "Organisation",
  group: "Group",
  global: "Global",
  ideaBusinessProfile: "Business profile",
  challenge: "Challenge",
};

export type RoleUsersQueryParams =
  OpenAPI.GET<"/roles/{for_type}/{for_id}/roles/{role_id}/users/assigned">["parameters"]["query"] & {
    query?: string;
  };

export type RoleUsersPage = {
  users: OpenAPI.Schemas["users"][];
  total: number;
  nextPageAvailable: boolean;
  prevPageAvailable: boolean;
};

export type PotentialRoleUsersResponse =
  OpenAPI.GET<"/roles/{for_type}/{for_id}/roles/{role_id}/users/potential">["response"];

export type PotentialRoleUser = PotentialRoleUsersResponse["users"][number] & {
  _id: string;
};

export type PotentialRoleUsersPage = Omit<
  PotentialRoleUsersResponse,
  "users"
> & { users: PotentialRoleUser[] };

export type RoleAssignScope = {
  for_type: "organisation" | "group" | "challenge";
  for_id: string;
};

export type RolesGetPathParams =
  OpenAPI.GET<"/roles/{for_type}/{for_id}/roles">["parameters"]["path"];

export type RoleSettingsUpdateBody = NonNullable<
  OpenAPI.POST<"/roles/{for_type}/{for_id}/roles/{role_id}">["requestBody"]
>;

export type RoleOwnerOrganisation = NonNullable<
  NonNullable<RolesUser>["ownerOrganisation"]
>;

export type RoleEditorIconOption = SemanticICONS;
export type RoleEditorColourOption = SemanticCOLORS;

export type RolesSetState<T> = Dispatch<SetStateAction<T>>;

export const isRoleIconName = (icon: SemanticICONS): icon is RoleIconName =>
  (
    ["certificate", "star", "trophy", "rocket", "diamond"] as RoleIconName[]
  ).includes(icon as RoleIconName);
